import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Stickyfill from 'stickyfilljs';
import { groupBy, map, flatten, mapValues, max, each, size, omit, every } from 'lodash';
import { removeFromSessionStorage } from '@codex/helpers';
import { useToggle } from '@codex/hooks';
import { Icon } from '@codex/icons';
import { dispatchUncheckProduct } from '../../../analytics';
import {
  CompareTable,
  StyledTr,
  Cell,
  THead,
  ProductImage,
  ProductTitle,
  ProductTitleLink,
  RemoveButton,
  CompareProductTitleContainer,
  Separator,
  ShowDifferences,
  SectionHeader,
  Chevron,
  StickyTable
} from './CompareProductStyleTypes';
import CompareTableTypes from './CompareTableTypes';

const HEADER_COLUMN_LABELS = {
  AddToBasket: 'Add to Basket',
  Price: 'Price',
  Availability: 'Availability',
  CustomerRating: 'Customer Rating'
};

const processCompareData = (allProductsToCompare) => {
  const productList = [];
  map(allProductsToCompare, (product) => productList.push(...product));
  const allProductsGroupedByLabel = groupBy(productList, 'groupLabel');
  const compareTableDataForUI = mapValues(allProductsGroupedByLabel, (productGroupedByLabel) => {
    const productSpecDetails = map(productGroupedByLabel, (overView) => overView.specifications);
    const productHaveingAllSpec = max(productSpecDetails, size);
    const productSpecNames = map(productHaveingAllSpec, (productSpecItem) => productSpecItem.label);
    const productSpecDetailsUpdated = map(productSpecDetails, (productSpecDetailsItem) => {
      const productSpecDetailsWithPlaceHolders = [];
      each(productSpecNames, (productSpecName, index) => {
        const productSpecDetailsItemValue = productSpecDetailsItem[index];
        if (productSpecDetailsItemValue && productSpecDetailsItemValue.label === productSpecName) {
          productSpecDetailsWithPlaceHolders.push(productSpecDetailsItemValue);
        } else {
          productSpecDetailsWithPlaceHolders.push({ label: productSpecNames[index], value: '-' });
        }
      });
      return productSpecDetailsWithPlaceHolders;
    });
    const productSpecDetailsFlattened = flatten(productSpecDetailsUpdated);
    const productSpecDetailsGrouped = groupBy(productSpecDetailsFlattened, 'label');

    const productSpecDetailsWithHighlight = mapValues(productSpecDetailsGrouped, (rowItem) => {
      const allCellValuesAreSame =
        every(rowItem, (valueItem) => valueItem.value === rowItem[0].value);
      return { rowItem: rowItem, highlight: !allCellValuesAreSame };
    });
    return productSpecDetailsWithHighlight;
  });
  return compareTableDataForUI;
};

const ProductCompareTitleSection = (props) => {
  const {
    productTitle, productImgUrl, productId, handleRemoveItem
  } = props;
  const isIE11 = /Trident.*rv[ :]*11\./.test(navigator.userAgent);
  return (
    <CompareProductTitleContainer>
      {
        !isIE11 && (
        <RemoveButton
          onClick={handleRemoveItem(productId)}
          className={'compare-popup-remove-product'}
        >
          Remove
          {' '}
          <Icon icon={'Cross'} />
        </RemoveButton>
        )
}
      <ProductTitleLink href={`/GBUK/product-${productId}-pdt.html`}>
        <ProductImage src={productImgUrl} alt={productTitle} />
        <ProductTitle>{productTitle}</ProductTitle>
      </ProductTitleLink>
    </CompareProductTitleContainer>
  );
};

ProductCompareTitleSection.propTypes = {
  productTitle: PropTypes.string.isRequired,
  productImgUrl: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  handleRemoveItem: PropTypes.func.isRequired
};

const RenderTechSpecRows = ({
  highlight, featureItem, featureItemKey, productListLength
}) => (
  <StyledTr hasHover className={highlight ? 'highlighted' : ''}>
    <THead data-testid={featureItemKey}>{featureItemKey}</THead>
    {[...Array(productListLength).keys()].map((i) => (
      <Cell key={i}>{featureItem.rowItem[i] ? featureItem.rowItem[i].value : '-'}</Cell>
    ))}
  </StyledTr>
);
RenderTechSpecRows.propTypes = {
  highlight: PropTypes.bool.isRequired,
  featureItem: PropTypes.shape({
    highlight: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    rowItem: PropTypes.array.isRequired
  }).isRequired,
  featureItemKey: PropTypes.string.isRequired,
  productListLength: PropTypes.number.isRequired
};
export const CompareProduct = ({
  compareProductList,
  onRemoveItem,
  onRemoveItemCompareBar,
  productSegmentId,
  productIdForFailedAddToBasket
}) => {
  const [highlight, setHighlight] = useToggle(true);
  const productListLength = Object.keys(compareProductList).length;

  const columnTypeHeader = (val, index) => {
    const HeaderType = CompareTableTypes[val.label];
    const isAddToBasketButton = val.label === 'AddToBasket';
    const error = (isAddToBasketButton && productIdForFailedAddToBasket === val.value)
      ? { error: true }
      : {};

    return (
      <Cell key={index}>
        {HeaderType && <HeaderType val={val} {...error} />}
        {' '}
      </Cell>
    );
  };

  const stickyTableRef = useRef(null);
  const [closedSections, setClosedSections] = useState({});

  useEffect(() => {
    Stickyfill.addOne(stickyTableRef);
  }, []);

  const handleToggleSection = (sectionKey) => () => {
    if (sectionKey in closedSections) {
      setClosedSections(omit(closedSections, [sectionKey]));
    } else {
      setClosedSections({ ...closedSections, [sectionKey]: sectionKey });
    }
  };

  const renderHeaderDepictionCell = (val, cellIndex) => {
    const handleRemoveItem = (productId) => (e) => {
      e.preventDefault();

      if (productListLength === 1) {
        document.body.style.overflowY = 'unset';
      }

      onRemoveItem(productId);
      onRemoveItemCompareBar({
        product: { id: productId },
        productSegmentId: productSegmentId
      });

      dispatchUncheckProduct({ id: productId });
      removeFromSessionStorage({ id: productId });
    };
    return (
      <Cell style={{ paddingTop: '0' }} key={cellIndex}>
        <ProductCompareTitleSection
          productImgUrl={val.value[0]}
          productTitle={val.value[1]}
          productId={val.value[2]}
          handleRemoveItem={handleRemoveItem}
        />
      </Cell>
    );
  };

  const compareTableDataForUI = processCompareData(compareProductList);

  const { INFO: info, ...rest } = compareTableDataForUI;

  return (
    <>
      <StickyTable className={'compare-pop-up-sticky-table'} ref={stickyTableRef}>
        <tbody key={'INFO'}>
          {map(info, (featureItem, mapKey) => {
            if (mapKey === 'header') {
              return (
                <tr key={mapKey}>
                  <Cell style={{ padding: '15px' }}>
                    <p>Here are the items you`ve chosen to compare. </p>
                    <p>
                      For more help deciding, why not view our
                      <a href={'https://www.currys.co.uk/gbuk/buying-guides-1022-commercial.html'} target={'_blank'} rel={'noopener noreferrer'}>Buying Guide</a>
                    </p>
                    <Separator />
                    <ShowDifferences
                      data-interaction={`Show differences - ${highlight ? 'Yes' : 'No'}`}
                      label={highlight ? 'Yes' : 'No'}
                      active={highlight}
                      onClick={(e) => {
                        setHighlight(!highlight);
                        e.preventDefault();
                      }}
                    >
                      Show Differences
                    </ShowDifferences>
                  </Cell>
                  {map(featureItem.rowItem, renderHeaderDepictionCell)}
                </tr>
              );
            }
            return (
              <StyledTr hasHover={mapKey !== 'AddToBasket'} key={mapKey}>
                <THead>{HEADER_COLUMN_LABELS[mapKey]}</THead>
                {map(featureItem.rowItem, columnTypeHeader)}
              </StyledTr>
            );
          })}
        </tbody>
      </StickyTable>
      <CompareTable>
        {
          map(rest, (feature, featureKey) => {
            const chevronUp = !closedSections[featureKey] ? 180 : 0;
            return (
              <tbody key={featureKey}>
                <SectionHeader>
                  <th
                    data-testid={featureKey}
                    onClick={handleToggleSection(featureKey)}
                    colSpan={'5'}
                  >
                    <Chevron icon={'ArrowDown'} rotate={chevronUp} />
                    {featureKey}
                  </th>
                </SectionHeader>
                {!closedSections[featureKey] && map(feature, (featureItem, featureItemKey) =>
                  (
                    <RenderTechSpecRows
                      key={featureItemKey}
                      featureItemKey={featureItemKey}
                      highlight={highlight && featureItem.highlight}
                      featureItem={featureItem}
                      productListLength={productListLength}
                    />
                  ))}
              </tbody>
            );
          })
        }
      </CompareTable>
    </>
  );
};

CompareProduct.propTypes = {
  compareProductList: PropTypes.objectOf(PropTypes.array).isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  onRemoveItemCompareBar: PropTypes.func.isRequired,
  productSegmentId: PropTypes.number.isRequired,
  productIdForFailedAddToBasket: PropTypes.string
};

CompareProduct.defaultProps = {
  productIdForFailedAddToBasket: null
};

